"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AuthService from "../../services/authService";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Logo from "../../assets/LogoPdf.png";
import BackgroundImage from "../../assets/background.jpg";
import MfaSetupModal from "./components/mfaSetupModal";
import RegisterAccountModal from "./components/registerAccountModal";

const LoginForm = () => {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [mfaModal, setMfaModal] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState("");
  const [loadingMfa, setLoadingMfa] = useState(false);
  const [mfaStep, setMfaStep] = useState(2);
  const [mfaQrUrl, setMfaQrUrl] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  // El modo (login vs registro) se deriva de la URL: /registro = crear cuenta,
  // cualquier otra (/login) = iniciar sesión. Así cada uno tiene su propia ruta.
  const showRegister = location.pathname === "/registro";
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const handleCloseSnackbar = () => {
    setError(false);
    setSnackbarSeverity("error");
  };

  const openSnackbar = (message, severity = "error") => {
    setErrorMessage(message);
    setSnackbarSeverity(severity);
    setError(true);
  };

  // Al volver de /registro tras crear la cuenta, mostramos el aviso que viene en
  // el estado de navegación y lo limpiamos para que no se repita al refrescar.
  useEffect(() => {
    if (location.state?.notice) {
      openSnackbar(location.state.notice, "success");
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    if (!user || !password) {
      openSnackbar("Ingresá tu email y contraseña.");
      return;
    }

    setLoadingLogin(true);

    try {
      const login = await AuthService.singIn({
        name: user,
        password: password,
      });

      if (login.status === 200 && login.data.token) {
        navigate("/home");
      }
    } catch (error) {
      const data = error.response?.data;

      if (data?.error === "mfa_setup_required") {
        try {
          const factor = await AuthService.enrollMfa();
          setMfaQrUrl(factor.totp.uri);
          setMfaSecret(factor.totp.secret);
          setMfaFactorId(factor.id);
          setMfaStep(1);
          setMfaModal(true);
        } catch {
          openSnackbar("No se pudo iniciar la configuración de 2FA.");
        }
      } else if (data?.error === "mfa_required" && data?.factorId) {
        setMfaFactorId(data.factorId);
        setMfaStep(2);
        setMfaModal(true);
      } else if (error.response?.status === 401) {
        openSnackbar("Email o contraseña incorrectos.");
      }
      setLoadingLogin(false);
    }
  }

  const handleMfaVerify = async (code) => {
    setLoadingMfa(true);
    try {
      const response = await AuthService.verifyMfaCode(mfaFactorId, code);
      if (response.status === 200 && response.data.token) {
        setMfaModal(false);
        navigate("/home");
        return true;
      }
      return false;
    } catch (err) {
      openSnackbar("El código no es válido. Revisalo e intentá de nuevo.");
      setLoadingMfa(false);
      return false;
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f8f9fa",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: "#e0e0e0",
        transition: "border-color 0.2s ease",
      },
      "&:hover fieldset": {
        borderColor: "#b0b0b0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2563EB",
        borderWidth: "2px",
      },
      "&.Mui-focused": {
        backgroundColor: "#fff",
      },
    },
    "& .MuiInputLabel-root": {
      color: "#888",
      "&.Mui-focused": {
        color: "#2563EB",
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        backgroundImage: `url(${BackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
      }}
    >
      {/* Overlay oscuro */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 0,
        }}
      />

      {/* Panel izquierdo - Branding */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
          px: 6,
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 420 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: "#fff",
              mb: 2,
              letterSpacing: "-0.02em",
              textShadow: "0 2px 12px rgba(0,0,0,0.3)",
            }}
          >
            Voras WMS
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontWeight: 400,
              lineHeight: 1.6,
              textShadow: "0 1px 6px rgba(0,0,0,0.2)",
            }}
          >
            Gestión inteligente de almacenes.
            <br />
            Control total de tu inventario.
          </Typography>
        </Box>
      </Box>

      {/* Panel derecho - Formulario */}
      <Box
        sx={{
          width: { xs: "100%", md: 480 },
          minWidth: { md: 480 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
          backgroundColor: "rgba(255, 255, 255, 0.97)",
          backdropFilter: "blur(20px)",
          px: { xs: 3, sm: 5 },
          py: 4,
          boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 380 }}>
          {/* Logo */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <img src={Logo} alt="Voras" style={{ height: 56 }} />
          </Box>

          {/* Título */}
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              color: "#1a1a1a",
              mb: 0.5,
            }}
          >
            {showRegister ? "Crear cuenta" : "Bienvenido"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "#888",
              mb: 4,
              fontSize: "0.9rem",
            }}
          >
            {showRegister
              ? "Completá tus datos para registrarte"
              : "Ingresá tus credenciales para continuar"}
          </Typography>

          {showRegister ? (
            <RegisterAccountModal
              onClose={() => navigate("/login")}
              onSuccess={() =>
                navigate("/login", {
                  state: {
                    notice:
                      "Cuenta creada. Te enviamos un mail para confirmar tu cuenta; confirmala y luego iniciá sesión.",
                  },
                })
              }
              onError={(msg) => openSnackbar(msg)}
              inputSx={inputSx}
            />
          ) : (
          /* Formulario */
          <Box
            component="form"
            onSubmit={handleLogin}
            autoComplete="off"
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <input
              type="text"
              name="fakeusernameremembered"
              style={{ display: "none" }}
            />
            <input
              type="password"
              name="fakepasswordremembered"
              style={{ display: "none" }}
            />

            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, ml: 0.5 }}
            >
              Email
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="Ingresá tu email"
              autoComplete="off"
              value={user}
              name="login_user_fake"
              onChange={(e) => setUser(e.target.value)}
              size="small"
              sx={{ ...inputSx, mb: 2.5 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: "#999", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#555", mb: 0.5, ml: 0.5 }}
            >
              Contraseña
            </Typography>
            <TextField
              fullWidth
              placeholder="Ingresá tu contraseña"
              autoComplete="off"
              name="login_pass_fake"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="small"
              sx={{ ...inputSx, mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: "#999", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      size="small"
                      sx={{ color: "#999" }}
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
              <Typography
                variant="caption"
                onClick={handleForgotPassword}
                sx={{
                  color: "#2563EB",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  transition: "color 0.2s",
                  "&:hover": {
                    color: "#1D4ED8",
                    textDecoration: "underline",
                  },
                }}
              >
                ¿Olvidaste tu contraseña?
              </Typography>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loadingLogin}
              disableElevation
              sx={{
                borderRadius: "12px",
                padding: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                backgroundColor: "#2563EB",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#1D4ED8",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0, 82, 204, 0.3)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#b3c9e8",
                },
                mb: 3,
              }}
            >
              {loadingLogin ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Iniciar sesión"
              )}
            </Button>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography
                variant="body2"
                sx={{ color: "#888", fontSize: "0.85rem" }}
              >
                ¿No tenés cuenta?{" "}
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate("/registro");
                    }
                  }}
                  onClick={() => navigate("/registro")}
                  sx={{
                    color: "#2563EB",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "color 0.2s",
                    "&:hover": { color: "#1D4ED8", textDecoration: "underline" },
                  }}
                >
                  Registrate
                </Box>
              </Typography>
            </Box>
          </Box>
          )}
        </Box>

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            bottom: 20,
            color: "#bbb",
            fontSize: "0.7rem",
          }}
        >
          © {new Date().getFullYear()} Voras · Todos los derechos reservados
        </Typography>
      </Box>

      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "10px",
            fontWeight: 500,
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <MfaSetupModal
        open={mfaModal}
        onClose={() => setMfaModal(false)}
        qrUrl={mfaQrUrl}
        secret={mfaSecret}
        onVerify={handleMfaVerify}
        loading={loadingMfa}
        step={mfaStep}
      />
    </Box>
  );
};

export default LoginForm;
